import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { ENDPOINTS } from "@/api/application-api";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { ToastType } from "@/enums";
import { useAuth } from "@/context/auth-context";
import { jsonAuthHeaders } from "@/utils/headers";
import { LOCAL_STORAGE } from "@/constants";

export interface VerifyEmailCodeRequest {
  code: string;
  email: string;
  pending_authentication_token?: string;
}

export interface VerifyEmailCodeResponse {
  success: boolean;
  message: string;
  verification_token?: string;
  email: string;
  access_token?: string;
  user?: {
    id: number;
    email: string;
    name: string;
    verified: boolean;
  };
}

export const useVerifyEmailCode = () => {
  const auth = useAuth();
  const navigate = useNavigate();

  return useMutation(
    ["verify-email-code"],
    async (data: VerifyEmailCodeRequest): Promise<VerifyEmailCodeResponse> => {
      const response = await fetch(ENDPOINTS.auth.verify_email_code, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      return handleResponse(response);
    },
    {
      onError: (err: string) => {
        displayErrorToast(err || "Failed to verify email code");
      },
      onSuccess: data => {
        displayToast(data.message || "Email verified successfully", ToastType.success);
        fetch(ENDPOINTS.get_user, {
          headers: jsonAuthHeaders(auth?.userToken ?? ""),
        })
          .then(res => (res.ok ? res.json() : Promise.reject()))
          .then(user => {
            localStorage.removeItem(LOCAL_STORAGE.USER);
            auth?.updateUserState(user);
            navigate("/onboarding");
          })
          .catch(() => displayErrorToast("Failed to get user"));
      },
    },
  );
};
